/**
 * Modal for creating or editing a Portfolio Group.
 *
 * A portfolio group is a named set of inclusion rules at different hierarchy
 * levels. Each rule adds offices: "all offices under MAJCOM X" or "this
 * specific office". The union of all rules = the portfolio.
 */
import { useState, useEffect, useMemo, useCallback } from 'react'
import { X, Plus, Building2, Save, Search } from 'lucide-react'
import { execAPI } from '../../services/exec'
import { govdataAPI } from '../../services/govdata'
import type { PortfolioGroup, PortfolioGroupRule } from '../../types/exec'
import type { DimOffice } from '../../types/govdata'

interface Props {
  /** Existing group to edit, or null to create new */
  group: PortfolioGroup | null
  onClose: () => void
}

type HierarchyLevel = 'sub_agency' | 'macom' | 'subcom1' | 'office'

const LEVEL_LABELS: Record<HierarchyLevel, string> = {
  sub_agency: 'Sub-Agency (Branch / COCOM)',
  macom: 'Major Command (MAJCOM)',
  subcom1: 'Sub-Command',
  office: 'Office',
}

interface EntityOption {
  code: string
  name: string
  level: HierarchyLevel
  officeCount?: number
}

export default function PortfolioGroupEditor({ group, onClose }: Props) {
  const isNew = !group

  const [name, setName] = useState(group?.name || '')
  const [description, setDescription] = useState(group?.description || '')
  const [rules, setRules] = useState<PortfolioGroupRule[]>(group?.rules || [])
  const [saving, setSaving] = useState(false)
  const [resolvedCount, setResolvedCount] = useState<number | null>(group?.resolved_office_count ?? null)

  // Entity browser state
  const [addLevel, setAddLevel] = useState<HierarchyLevel>('macom')
  const [searchQuery, setSearchQuery] = useState('')
  const [dimOffices, setDimOffices] = useState<DimOffice[]>([])

  // Load dim_offices for the entity picker
  useEffect(() => {
    govdataAPI.dimOffices().then((resp) => setDimOffices(resp.results)).catch(console.error)
  }, [])

  // Build entity options from dim_offices, grouped by hierarchy level
  const entityOptions = useMemo((): EntityOption[] => {
    const map = new Map<string, EntityOption>()

    for (const o of dimOffices) {
      if (addLevel === 'sub_agency' && o.sub_agency_code && o.sub_agency_name) {
        const key = `sub_agency:${o.sub_agency_code}`
        if (!map.has(key)) {
          map.set(key, { code: o.sub_agency_code, name: o.sub_agency_name, level: 'sub_agency', officeCount: 0 })
        }
        map.get(key)!.officeCount = (map.get(key)!.officeCount || 0) + 1
      }
      if (addLevel === 'macom' && o.macom_code && o.macom_name) {
        const key = `macom:${o.macom_code}`
        if (!map.has(key)) {
          map.set(key, { code: o.macom_code, name: o.macom_name, level: 'macom', officeCount: 0 })
        }
        map.get(key)!.officeCount = (map.get(key)!.officeCount || 0) + 1
      }
      if (addLevel === 'subcom1' && o.subcom1_code && o.subcom1_name) {
        const key = `subcom1:${o.subcom1_code}`
        if (!map.has(key)) {
          map.set(key, { code: o.subcom1_code, name: o.subcom1_name, level: 'subcom1', officeCount: 0 })
        }
        map.get(key)!.officeCount = (map.get(key)!.officeCount || 0) + 1
      }
      if (addLevel === 'office' && o.office_code && o.office_name) {
        map.set(`office:${o.office_code}`, {
          code: o.office_code,
          name: o.office_name,
          level: 'office',
          officeCount: 1,
        })
      }
    }

    let options = Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name))

    // Filter by search
    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      options = options.filter(
        (o) => o.name.toLowerCase().includes(q) || o.code.toLowerCase().includes(q),
      )
    }

    // Exclude already-added rules
    const existingKeys = new Set(rules.map((r) => `${r.hierarchy_level}:${r.entity_code}`))
    options = options.filter((o) => !existingKeys.has(`${o.level}:${o.code}`))

    return options.slice(0, 100) // cap for performance
  }, [dimOffices, addLevel, searchQuery, rules])

  // Add a rule (locally if new, or via API if editing)
  const handleAddRule = useCallback(async (entity: EntityOption) => {
    if (group) {
      // Editing existing group — add via API
      try {
        const updated = await execAPI.addPortfolioGroupRule(group.id, {
          hierarchy_level: entity.level,
          entity_code: entity.code,
          entity_name: entity.name,
        })
        setRules(updated.rules)
        // Refresh resolved count
        const resolved = await execAPI.resolvePortfolioGroup(group.id)
        setResolvedCount(resolved.total_offices)
      } catch (err) {
        console.error('Failed to add rule:', err)
      }
    } else {
      // New group — add locally (will be saved on create)
      setRules((prev) => [
        ...prev,
        {
          id: `temp-${Date.now()}`,
          hierarchy_level: entity.level,
          entity_code: entity.code,
          entity_name: entity.name,
        },
      ])
    }
  }, [group])

  // Remove a rule
  const handleRemoveRule = useCallback(async (ruleId: string) => {
    if (group && !ruleId.startsWith('temp-')) {
      try {
        const updated = await execAPI.removePortfolioGroupRule(group.id, ruleId)
        setRules(updated.rules)
        const resolved = await execAPI.resolvePortfolioGroup(group.id)
        setResolvedCount(resolved.total_offices)
      } catch (err) {
        console.error('Failed to remove rule:', err)
      }
    } else {
      setRules((prev) => prev.filter((r) => r.id !== ruleId))
    }
  }, [group])

  // Save (create new or update existing)
  const handleSave = async () => {
    if (!name.trim()) return
    setSaving(true)
    try {
      if (group) {
        // Update name/description
        await execAPI.updatePortfolioGroup(group.id, { name, description })
      } else {
        // Create new group + add rules
        const newGroup = await execAPI.createPortfolioGroup({ name, description })
        for (const rule of rules) {
          await execAPI.addPortfolioGroupRule(newGroup.id, {
            hierarchy_level: rule.hierarchy_level,
            entity_code: rule.entity_code,
            entity_name: rule.entity_name,
          })
        }
      }
      onClose()
    } catch (err) {
      console.error('Failed to save group:', err)
    } finally {
      setSaving(false)
    }
  }

  const selectClass = 'w-full px-3 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            {isNew ? 'New Portfolio Group' : `Edit: ${group.name}`}
          </h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {/* Name + Description */}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 sm:col-span-1">
              <label className="block text-xs font-medium text-gray-500 mb-1">Group Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., PAE RAS Portfolio"
                className={selectClass}
                autoFocus
              />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <label className="block text-xs font-medium text-gray-500 mb-1">Description (optional)</label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What this group represents"
                className={selectClass}
              />
            </div>
          </div>

          {/* Current rules */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                Inclusion Rules
              </label>
              {resolvedCount !== null && (
                <span className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400">
                  <Building2 size={12} />
                  {resolvedCount} offices resolved
                </span>
              )}
            </div>

            {rules.length === 0 ? (
              <div className="text-sm text-gray-400 py-4 text-center border border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
                No rules yet. Add entities below to define the portfolio scope.
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {rules.map((rule) => (
                  <span
                    key={rule.id}
                    className="inline-flex items-center gap-1.5 pl-2.5 pr-1 py-1 rounded-full text-xs font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800"
                  >
                    <span className="text-[10px] text-blue-400 uppercase">{LEVEL_LABELS[rule.hierarchy_level as HierarchyLevel] || rule.hierarchy_level}</span>
                    {rule.entity_name || rule.entity_code}
                    <button
                      onClick={() => handleRemoveRule(rule.id)}
                      className="p-0.5 rounded-full hover:bg-blue-200 dark:hover:bg-blue-800 text-blue-400 hover:text-red-500"
                    >
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Add rule picker */}
          <div className="border-t border-gray-100 dark:border-gray-800 pt-4">
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
              Add Entities
            </label>

            <div className="flex gap-2 mb-2">
              {/* Level selector */}
              <select
                value={addLevel}
                onChange={(e) => { setAddLevel(e.target.value as HierarchyLevel); setSearchQuery('') }}
                className="px-2 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200"
              >
                <option value="sub_agency">Sub-Agencies (Branches / COCOMs)</option>
                <option value="macom">Major Commands (MAJCOM)</option>
                <option value="subcom1">Sub-Commands</option>
                <option value="office">Offices</option>
              </select>

              {/* Search */}
              <div className="flex-1 relative">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={`Search ${LEVEL_LABELS[addLevel].toLowerCase()}s...`}
                  className="w-full pl-8 pr-3 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Entity list */}
            <div className="max-h-48 overflow-y-auto border border-gray-100 dark:border-gray-800 rounded-lg">
              {entityOptions.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-4">
                  {searchQuery ? 'No matches' : 'All entities already added'}
                </p>
              ) : (
                entityOptions.map((entity) => (
                  <button
                    key={`${entity.level}:${entity.code}`}
                    onClick={() => handleAddRule(entity)}
                    className="w-full flex items-center justify-between px-3 py-1.5 text-left hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors border-b border-gray-50 dark:border-gray-800 last:border-b-0"
                  >
                    <div className="min-w-0">
                      <span className="text-xs text-gray-900 dark:text-gray-100">{entity.name}</span>
                      <span className="text-[10px] text-gray-400 ml-1.5">{entity.code}</span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 ml-2">
                      <span className="text-[10px] text-gray-400">{entity.officeCount} offices</span>
                      <Plus size={14} className="text-blue-500" />
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm rounded-lg text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            {isNew ? 'Cancel' : 'Close'}
          </button>
          <button
            onClick={handleSave}
            disabled={!name.trim() || saving}
            className="flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Save size={14} />
            {saving ? 'Saving...' : isNew ? 'Create Group' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  )
}
